(function () {
  if (window.TuyaFeaturedProductsInitialized) return;

  window.TuyaFeaturedProductsInitialized = true;

  const SECTION_SELECTOR = '[data-tuya-section="featured-products"]';
  const ITEM_SELECTOR = '.tuya-featured-products__item';
  const TRACK_SELECTOR = '.tuya-featured-products__track';
  const SENTINEL_SELECTOR = '.tuya-featured-products__sentinel';
  const PREV_SELECTOR = '.tuya-carousel-nav--prev';
  const NEXT_SELECTOR = '.tuya-carousel-nav--next';
  const REVEAL_DURATION = 500;
  const REVEAL_STAGGER = 60;
  const LOAD_AHEAD_PX = 1000;
  const LOAD_LOCK_MS = 650;

  const sectionCleanups = new WeakMap();

  function findSection(target) {
    if (!target) return null;

    return target.matches(SECTION_SELECTOR)
      ? target
      : target.querySelector(SECTION_SELECTOR);
  }

  function readNumber(value, fallback) {
    const number = parseInt(value, 10);

    return Number.isNaN(number) ? fallback : number;
  }

  function getSectionSetting(section, name, fallback) {
    return readNumber(section.dataset[name], fallback);
  }

  function getItems(section) {
    return Array.from(section.querySelectorAll(ITEM_SELECTOR));
  }

  function getTrackGap(track) {
    return parseFloat(window.getComputedStyle(track).gap) || 0;
  }

  function getItemsPerRow(track) {
    const columns = window
      .getComputedStyle(track)
      .gridTemplateColumns
      .split(' ')
      .filter((column) => column && column !== 'none');

    return Math.max(columns.length, 1);
  }

  function getLimitedItemCount(section) {
    return Math.max(getSectionSetting(section, 'tuyaLimit', getItems(section).length), 0);
  }

  function getBatchSize(section) {
    return Math.max(getSectionSetting(section, 'tuyaBatchSize', 2), 1);
  }

  function getNextPage(section) {
    return Math.floor(getItems(section).length / getBatchSize(section)) + 1;
  }

  function getSectionRenderUrl(section, page) {
    const url = new URL(window.location.href);

    url.searchParams.set('section_id', section.dataset.tuyaSectionId);
    url.searchParams.set('page', page);

    return url.toString();
  }

  function isNearViewport(element) {
    const rect = element.getBoundingClientRect();

    return rect.top < window.innerHeight + LOAD_AHEAD_PX && rect.bottom > -LOAD_AHEAD_PX;
  }

  function cleanupItems(section) {
    getItems(section).forEach((item) => {
      item.hidden = false;
      item.classList.remove('is-hidden', 'is-revealing');
      item.style.removeProperty('--reveal-delay');
    });
  }

  function appendFetchedItems(section, html) {
    const track = section.querySelector(TRACK_SELECTOR);
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fetchedSection = parsed.querySelector(SECTION_SELECTOR);
    const fetchedItems = fetchedSection
      ? Array.from(fetchedSection.querySelectorAll(ITEM_SELECTOR))
      : [];

    if (!track || fetchedItems.length === 0) return 0;

    const remainingSlots = getLimitedItemCount(section) - getItems(section).length;
    const itemsToAppend = fetchedItems.slice(0, Math.max(remainingSlots, 0));

    itemsToAppend.forEach((item) => {
      track.appendChild(document.importNode(item, true));
    });

    return itemsToAppend.length;
  }

  function createBatchLoader(section) {
    const loadedPages = new Set([1]);
    let loadingPromise = null;
    let hasMore = getItems(section).length < getLimitedItemCount(section);

    async function loadUntil(targetCount) {
      if (loadingPromise) {
        await loadingPromise;

        if (getItems(section).length >= targetCount || !hasMore) {
          return getItems(section).length;
        }
      }

      loadingPromise = (async function loadBatches() {
        while (hasMore && getItems(section).length < targetCount) {
          const currentCount = getItems(section).length;
          const requestedCount = Math.min(targetCount, getLimitedItemCount(section));
          const missingCount = requestedCount - currentCount;
          const pageCount = Math.ceil(missingCount / getBatchSize(section));
          const firstPage = getNextPage(section);
          const pages = [];

          for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
            const page = firstPage + pageIndex;

            if (!loadedPages.has(page)) {
              loadedPages.add(page);
              pages.push(page);
            }
          }

          if (pages.length === 0) break;

          const responses = await Promise.all(
            pages.map((page) =>
              fetch(getSectionRenderUrl(section, page), {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
              })
            )
          );

          if (responses.some((response) => !response.ok)) {
            hasMore = false;
            break;
          }

          const htmlResponses = await Promise.all(
            responses.map((response) => response.text())
          );
          const appendedCount = htmlResponses.reduce(
            (count, html) => count + appendFetchedItems(section, html),
            0
          );

          if (appendedCount === 0 || getItems(section).length >= getLimitedItemCount(section)) {
            hasMore = false;
          }
        }
      })();

      try {
        await loadingPromise;
      } finally {
        loadingPromise = null;
      }

      return getItems(section).length;
    }

    return {
      loadUntil,
      hasMore() {
        return hasMore;
      },
    };
  }

  function getScrollStep(track) {
    const firstItem = getItems(track).find((item) => !item.hidden);

    if (!firstItem) return track.clientWidth;

    return firstItem.getBoundingClientRect().width + getTrackGap(track);
  }

  function initCarousel(section) {
    if (section.dataset.tuyaDisplayMode !== 'carousel') return null;

    const track = section.querySelector(TRACK_SELECTOR);
    const prevBtn = section.querySelector(PREV_SELECTOR);
    const nextBtn = section.querySelector(NEXT_SELECTOR);

    if (!track || !prevBtn || !nextBtn) return null;

    let destroyed = false;

    function updateNavButtons() {
      const maxScroll = track.scrollWidth - track.clientWidth;

      prevBtn.disabled = track.scrollLeft <= 2;
      nextBtn.disabled = maxScroll <= 2 || track.scrollLeft >= maxScroll - 2;
    }

    function handlePrevClick() {
      track.scrollBy({
        left: -getScrollStep(track),
        behavior: 'smooth',
      });
    }

    function handleNextClick() {
      track.scrollBy({
        left: getScrollStep(track),
        behavior: 'smooth',
      });
    }

    function handleTrackScroll() {
      updateNavButtons();
    }

    prevBtn.addEventListener('click', handlePrevClick);
    nextBtn.addEventListener('click', handleNextClick);
    track.addEventListener('scroll', handleTrackScroll, { passive: true });
    window.addEventListener('resize', updateNavButtons);
    window.setTimeout(updateNavButtons, 100);

    return function cleanupCarousel() {
      destroyed = true;
      prevBtn.removeEventListener('click', handlePrevClick);
      nextBtn.removeEventListener('click', handleNextClick);
      track.removeEventListener('scroll', handleTrackScroll);
      window.removeEventListener('resize', updateNavButtons);
    };
  }

  function initInfiniteGrid(section, loader) {
    if (section.dataset.tuyaDisplayMode !== 'grid-infinite') return null;

    const track = section.querySelector(TRACK_SELECTOR);
    const sentinel = section.querySelector(SENTINEL_SELECTOR);

    if (!track || !sentinel) return null;

    section.classList.add('is-loading-initial-row');

    let visibleRows = 1;
    let isRevealing = false;
    let lastRevealScrollY = window.scrollY;
    let isInitialLoadComplete = false;
    let userInteracted = false;
    let destroyed = false;

    function getVisibleCount() {
      return Math.min(getLimitedItemCount(section), visibleRows * getItemsPerRow(track));
    }

    function applyVisibleItems(fromIndex) {
      const visibleCount = getVisibleCount();
      let revealIndex = 0;

      getItems(section).forEach((item, index) => {
        const shouldShow = index < visibleCount;

        item.classList.toggle('is-hidden', !shouldShow);

        if (shouldShow && index >= fromIndex) {
          item.style.setProperty('--reveal-delay', `${revealIndex * REVEAL_STAGGER}ms`);
          item.classList.add('is-revealing');
          revealIndex++;

          window.setTimeout(() => {
            item.classList.remove('is-revealing');
            item.style.removeProperty('--reveal-delay');
          }, REVEAL_DURATION + revealIndex * REVEAL_STAGGER);
        } else {
          item.classList.remove('is-revealing');
          item.style.removeProperty('--reveal-delay');
        }
      });

      window.setTimeout(() => {
        isRevealing = false;
      }, LOAD_LOCK_MS);
    }

    async function ensureRowsLoaded(fromIndex) {
      const targetCount = getVisibleCount();
      const needsNetwork = getItems(section).length < targetCount && loader.hasMore();
      const isLoadingNextRow = fromIndex < targetCount && needsNetwork;

      if (isLoadingNextRow) {
        section.classList.add('is-loading-next-row');
      }

      try {
        await loader.loadUntil(targetCount);
      } finally {
        section.classList.remove('is-loading-next-row');
      }

      if (!destroyed) {
        applyVisibleItems(fromIndex);
      }
    }

    function revealNextRow() {
      if (isRevealing) return;
      if (!loader.hasMore() && getVisibleCount() >= getItems(section).length) return;

      const previousVisibleCount = getVisibleCount();

      visibleRows++;
      isRevealing = true;
      lastRevealScrollY = window.scrollY;
      ensureRowsLoaded(previousVisibleCount);
    }

    function handleResize() {
      ensureRowsLoaded(getItems(section).length);
    }

    function markUserInteraction() {
      userInteracted = true;
    }

    function handleScroll() {
      if (!isInitialLoadComplete) return;
      if (!userInteracted) return;

      const scrolledEnough = Math.abs(window.scrollY - lastRevealScrollY) > 24;

      if (scrolledEnough && isNearViewport(sentinel)) {
        revealNextRow();
      }
    }

    function handleWheel(event) {
      if (!isInitialLoadComplete || event.deltaY <= 0) return;

      markUserInteraction();

      if (isNearViewport(sentinel)) {
        revealNextRow();
      }
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('touchstart', markUserInteraction, { passive: true });
    window.addEventListener('keydown', markUserInteraction);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    ensureRowsLoaded(getItems(section).length).then(() => {
      if (destroyed) return;

      section.classList.remove('is-loading-initial-row');
      section.classList.add('is-ready');
      isInitialLoadComplete = true;
      lastRevealScrollY = window.scrollY;
    });

    return function cleanupInfiniteGrid() {
      destroyed = true;
      section.classList.remove('is-loading-initial-row');
      section.classList.remove('is-loading-next-row');
      section.classList.remove('is-ready');
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('touchstart', markUserInteraction);
      window.removeEventListener('keydown', markUserInteraction);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
    };
  }

  function destroySection(section) {
    const cleanup = sectionCleanups.get(section);

    if (cleanup) cleanup();

    cleanupItems(section);
    sectionCleanups.delete(section);
  }

  function initSection(section) {
    if (!section) return;

    destroySection(section);

    const loader = section.dataset.tuyaDisplayMode === 'grid-infinite'
      ? createBatchLoader(section)
      : null;
    const cleanupCarousel = initCarousel(section);
    const cleanupInfiniteGrid = loader ? initInfiniteGrid(section, loader) : null;

    sectionCleanups.set(section, function cleanupSection() {
      if (cleanupCarousel) cleanupCarousel();
      if (cleanupInfiniteGrid) cleanupInfiniteGrid();
    });
  }

  function initAllSections() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSections);
  } else {
    initAllSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSection(findSection(event.target));
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = findSection(event.target);

    if (section) destroySection(section);
  });
})();
